import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/empty-state'
import { cn } from '@/lib/utils'
import { useDashboardPipeline } from './use-dashboard'
import { GitBranch } from 'lucide-react'

const STAGE_COLORS: readonly string[] = [
  'bg-slate-500',
  'bg-blue-500',
  'bg-cyan-500',
  'bg-amber-500',
  'bg-purple-500',
  'bg-emerald-500',
]

function PipelineSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-6 w-full rounded-full" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-1">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-5 w-10" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function PipelineOverview() {
  const { data: pipeline, isLoading } = useDashboardPipeline()

  const totalCount = pipeline?.reduce((sum, stage) => sum + stage.count, 0) ?? 0
  const isEmpty = totalCount === 0

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pipeline Overview</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <PipelineSkeleton />
        ) : isEmpty ? (
          <EmptyState
            icon={GitBranch}
            title="No leads in pipeline"
            description="Import leads to see your pipeline funnel take shape."
          />
        ) : (
          <div className="space-y-4">
            {/* Horizontal stacked bar */}
            <div className="flex h-6 w-full overflow-hidden rounded-full bg-muted">
              {pipeline?.map((stage, i) => {
                if (stage.count === 0) return null
                const widthPercent = (stage.count / totalCount) * 100

                return (
                  <div
                    key={stage.name}
                    className={cn(
                      STAGE_COLORS[i % STAGE_COLORS.length],
                      'transition-all duration-500',
                      i === 0 && 'rounded-l-full',
                      i === (pipeline?.length ?? 0) - 1 && 'rounded-r-full',
                    )}
                    style={{ width: `${widthPercent}%` }}
                    title={`${stage.name}: ${stage.count} (${stage.percentage}%)`}
                  />
                )
              })}
            </div>

            {/* Stage breakdown */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
              {pipeline?.map((stage, i) => (
                <div key={stage.name} className="flex items-center gap-2">
                  <div
                    className={cn(
                      'h-2.5 w-2.5 rounded-full shrink-0',
                      STAGE_COLORS[i % STAGE_COLORS.length],
                    )}
                  />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground truncate">{stage.name}</p>
                    <p className="text-sm font-semibold">
                      {stage.count}{' '}
                      <span className="text-xs font-normal text-muted-foreground">
                        ({stage.percentage}%)
                      </span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
